import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Select } from './Input';

// Stronicowanie z oknem numerów stron.
//
// Poprzednia wersja renderowała przycisk dla KAŻDEJ strony
// (Array.from({length: totalPages})), więc przy 2000 kontenerów i 15 na stronę
// powstawało 134 przycisków rozjeżdżających układ. Tutaj widoczne są zawsze
// pierwsza, ostatnia i sąsiedztwo bieżącej, a reszta zwija się w wielokropek.

const PER_PAGE_OPTIONS = [15, 25, 50, 100];

/** Numery stron do pokazania; `null` oznacza wielokropek. */
const buildPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const pages = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(total - 1, current + 1);

  if (from > 2) pages.push(null);
  for (let page = from; page <= to; page += 1) pages.push(page);
  if (to < total - 1) pages.push(null);

  pages.push(total);
  return pages;
};

export function Pagination({
  page,
  totalPages,
  onPageChange,
  perPage,
  onPerPageChange,
  rangeFrom,
  rangeTo,
  total,
  itemLabel = 'pozycji',
}) {
  const pages = buildPageWindow(page, totalPages);

  const navButton = 'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-3 py-2.5">
      <p className="tabular text-xs text-muted">
        {total === 0 ? (
          `Brak ${itemLabel}`
        ) : (
          <>
            <span className="font-medium text-content">
              {rangeFrom}–{rangeTo}
            </span>{' '}
            z <span className="font-medium text-content">{total}</span> {itemLabel}
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        {totalPages > 1 && (
          <nav aria-label="Stronicowanie" className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              aria-label="Poprzednia strona"
              className={cn(navButton, 'text-muted hover:bg-elevated hover:text-content')}
            >
              <ChevronLeft size={15} />
            </button>

            {pages.map((pageNumber, index) =>
              pageNumber === null ? (
                <span key={`gap-${index}`} className="px-1 text-xs text-subtle">
                  …
                </span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  aria-current={pageNumber === page ? 'page' : undefined}
                  className={cn(
                    navButton,
                    'tabular',
                    pageNumber === page
                      ? 'bg-accent text-accent-content'
                      : 'text-muted hover:bg-elevated hover:text-content'
                  )}
                >
                  {pageNumber}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              aria-label="Następna strona"
              className={cn(navButton, 'text-muted hover:bg-elevated hover:text-content')}
            >
              <ChevronRight size={15} />
            </button>
          </nav>
        )}

        <Select
          value={perPage}
          onChange={(event) => onPerPageChange(event.target.value)}
          aria-label="Liczba wierszy na stronę"
          className="h-8 w-[4.5rem] text-xs"
        >
          {PER_PAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

export default Pagination;
